// Chakra imports
import {
  Box,
  Text,
  SimpleGrid,
  useColorModeValue,
  Icon,
  Flex,
  Spinner,
  Alert,
  AlertIcon,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  FormControl,
  FormLabel,
  Select,
  useToast,
} from "@chakra-ui/react";
// Custom components
import MiniStatistics from "components/card/MiniStatistics";
import IconBox from "components/icons/IconBox";
import React, { useState, useEffect } from "react";
import {
  MdPerson,
  MdSchool,
  MdAttachMoney,
  MdCheckCircle,
  MdFileDownload,
} from "react-icons/md";
import { formatNumber, formatDate, formatStatus } from "utils/format";
import tutorsService from "services/tutorsService";
import TutorsTable from "./components/TutorsTable";
import { exportToCSV } from "utils/export";

export default function TutorsManagement() {
  const toast = useToast();
  
  // Chakra Color Mode - All hooks must be called at the top level
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const cardBg = useColorModeValue('white', 'navy.800');
  const brandColor = useColorModeValue('brand.500', 'white');
  const boxBg = useColorModeValue('secondaryGray.300', 'whiteAlpha.100');

  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    byCenter: {},
  });
  const [tutors, setTutors] = useState([]);
  const [tutorsLoading, setTutorsLoading] = useState(true);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    itemsPerPage: 10,
    total: 0,
  });
  const [exportLoading, setExportLoading] = useState(false);
  const [exportType, setExportType] = useState('pdf');
  const [exportFilter, setExportFilter] = useState('all');
  
  // Export Modal
  const { isOpen: isExportOpen, onOpen: onExportOpen, onClose: onExportClose } = useDisclosure();

  // Fetch tutors data
  useEffect(() => {
    const fetchTutors = async () => {
      setTutorsLoading(true);
      try {
        const skip = (pagination.currentPage - 1) * pagination.itemsPerPage;
        const result = await tutorsService.getTutors({
          skip,
          limit: pagination.itemsPerPage,
        });

        if (result.success && result.data) {
          setTutors(result.data);
          if (result.pagination) {
            setPagination(prev => ({
              ...prev,
              total: result.pagination.total,
            }));
            // Calculate stats from data
            const approved = result.data.filter(t => t.approve === 'approved').length;
            const pending = result.data.filter(t => t.approve === 'pending').length;
            const fields = new Set();
            result.data.forEach(tutor => {
              if (tutor.academic_field) {
                fields.add(tutor.academic_field);
              }
            });
            setStats({
              total: result.pagination.total,
              active: approved,
              inactive: pending,
              byCenter: Object.fromEntries(Array.from(fields).map(f => [f, 0])),
            });
          }
        } else {
          setError(result.error || 'Failed to load tutors');
        }
      } catch (err) {
        console.error('Error fetching tutors:', err);
        setError('Failed to load tutors. Please try again.');
      } finally {
        setTutorsLoading(false);
        setLoading(false);
      }
    };

    fetchTutors();
  }, [pagination.currentPage, pagination.itemsPerPage]);

  // Handle export
  const handleExport = async () => {
    setExportLoading(true);
    try {
      // Build filter params
      const filterParams = {};
      if (exportFilter !== 'all') {
        filterParams.approve = exportFilter;
      }

      if (exportType === 'pdf') {
        // Use backend PDF export endpoint
        const result = await tutorsService.exportTutorsPDF(filterParams);

        if (result.success) {
          toast({
            title: 'Success',
            description: 'Tutors exported successfully as PDF',
            status: 'success',
            duration: 5000,
            isClosable: true,
          });
          onExportClose();
        } else {
          toast({
            title: 'Error',
            description: result.error || 'Failed to export PDF',
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
        }
      } else {
        // CSV export - use frontend generation
        const result = await tutorsService.getAllTutors(filterParams);

        if (result.success && result.data) {
          const tutorsData = result.data;

          // Define columns for export
          const columns = [
            { key: 'name', label: 'Name' },
            { key: 'email', label: 'Email' },
            { key: 'phone', label: 'Phone' },
            { key: 'current_year', label: 'Current Year' },
            { key: 'academic_field', label: 'Academic Field' },
            { key: 'department_of_choice', label: 'Department' },
            { key: 'experience_at_mia', label: 'Experience at MIA' },
            { key: 'approve', label: 'Status' },
            { key: 'created_at', label: 'Application Date' },
          ];

          // Format data for export
          const formattedData = tutorsData.map(tutor => ({
            ...tutor,
            approve: formatStatus(tutor.approve),
            created_at: formatDate(tutor.created_at),
          }));

          // Generate filename
          const filterText = exportFilter === 'all' ? 'All' : formatStatus(exportFilter);
          const timestamp = new Date().toISOString().split('T')[0];
          const filename = `tutors_${filterText.toLowerCase()}_${timestamp}`;

          exportToCSV(
            formattedData,
            columns,
            `${filename}.csv`
          );

          toast({
            title: 'Success',
            description: 'Tutors exported successfully as CSV',
            status: 'success',
            duration: 5000,
            isClosable: true,
          });

          onExportClose();
        } else {
          toast({
            title: 'Error',
            description: result.error || 'Failed to export tutors',
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
        }
      }
    } catch (err) {
      console.error('Export error:', err);
      toast({
        title: 'Error',
        description: 'Failed to export tutors. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setExportLoading(false);
    }
  };

  if (loading) {
    return (
      <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
        <Box display="flex" justifyContent="center" alignItems="center" minH="400px">
          <Spinner size="xl" color="brand.500" />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
        <Alert status="error" borderRadius="15px">
          <AlertIcon />
          <Text color={textColor}>{error}</Text>
        </Alert>
      </Box>
    );
  }

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      <Flex direction='column' gap='20px'>
        <Flex justify='space-between' align='center' flexWrap='wrap' gap='15px'>
          <Text color={textColor} fontSize='2xl' fontWeight='700' mb='10px'>
            Tutors Management
          </Text>
          <Button
            onClick={onExportOpen}
            leftIcon={<Icon as={MdFileDownload} />}
            colorScheme='blue'
            variant='solid'>
            Export Applications
          </Button>
        </Flex>

        {/* Statistics Cards */}
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} gap='20px' mb='20px'>
          <MiniStatistics
            startContent={
              <IconBox
                w='56px'
                h='56px'
                bg={boxBg}
                icon={
                  <Icon w='32px' h='32px' as={MdPerson} color={brandColor} />
                }
              />
            }
            name='Total Tutors'
            value={formatNumber(stats.total)}
          />
          <MiniStatistics
            startContent={
              <IconBox
                w='56px'
                h='56px'
                bg={boxBg}
                icon={
                  <Icon w='32px' h='32px' as={MdCheckCircle} color='green.500' />
                }
              />
            }
            name='Active Tutors'
            value={formatNumber(stats.active)}
          />
          <MiniStatistics
            startContent={
              <IconBox
                w='56px'
                h='56px'
                bg={boxBg}
                icon={
                  <Icon w='32px' h='32px' as={MdSchool} color={brandColor} />
                }
              />
            }
            name='By Center'
            value={formatNumber(Object.keys(stats.byCenter || {}).length)}
          />
          <MiniStatistics
            startContent={
              <IconBox
                w='56px'
                h='56px'
                bg={boxBg}
                icon={
                  <Icon w='32px' h='32px' as={MdAttachMoney} color={brandColor} />
                }
              />
            }
            name='Inactive Tutors'
            value={formatNumber(stats.inactive)}
          />
        </SimpleGrid>

        {/* Tutors Table */}
        <Box mt="40px">
          {tutorsLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minH="200px">
              <Spinner size="lg" color="brand.500" />
            </Box>
          ) : (
            <TutorsTable 
              tableData={tutors} 
              pagination={pagination}
              onPageChange={(page) => setPagination(prev => ({ ...prev, currentPage: page }))}
              onItemsPerPageChange={(itemsPerPage) => setPagination(prev => ({ ...prev, itemsPerPage, currentPage: 1 }))}
            />
          )}
        </Box>
      </Flex>

      {/* Export Modal */}
      <Modal isOpen={isExportOpen} onClose={onExportClose} size="md" isCentered>
        <ModalOverlay />
        <ModalContent bg={cardBg}>
          <ModalHeader color={textColor} fontSize="2xl" fontWeight="700">
            Export Tutor Applications
          </ModalHeader>
          <ModalCloseButton color={textColor} />
          <ModalBody pb="24px">
            <Flex direction='column' gap='20px'>
              <FormControl>
                <FormLabel
                  display='flex'
                  ms='4px'
                  fontSize='sm'
                  fontWeight='500'
                  color={textColor}
                  mb='8px'>
                  Export Type
                </FormLabel>
                <Select
                  variant='main'
                  fontSize='sm'
                  value={exportType}
                  onChange={(e) => setExportType(e.target.value)}
                  mb='8px'
                >
                  <option value="pdf">PDF</option>
                  <option value="csv">CSV</option>
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel
                  display='flex'
                  ms='4px'
                  fontSize='sm'
                  fontWeight='500'
                  color={textColor}
                  mb='8px'>
                  Filter by Status
                </FormLabel>
                <Select
                  variant='main'
                  fontSize='sm'
                  value={exportFilter}
                  onChange={(e) => setExportFilter(e.target.value)}
                  mb='8px'
                >
                  <option value="all">All Applications</option>
                  <option value="approved">Approved</option>
                  <option value="pending">Pending</option>
                  <option value="rejected">Rejected</option>
                </Select>
              </FormControl>
            </Flex>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onExportClose}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleExport}
              isLoading={exportLoading}
              loadingText="Exporting">
              Export
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

